import React from 'react';
import BaseModal from '../common/BaseModal';
import { formatAmount } from '../../utils/formatters';
import { formatDate } from '../../utils/dateUtils';
import '../../styles/ViewIncomeModal.css';
import {
  DollarSign,
  Edit,
  Trash2,
  Calendar,
  RefreshCw,
  Users,
  User,
  EyeOff
} from 'lucide-react';

// Helper to calculate monthly equivalent
function calculateMonthlyAmount(amount, frequency) {
  const numAmount = parseFloat(amount) || 0;
  switch (frequency) {
    case 'weekly':
      return numAmount * 52 / 12;
    case 'biweekly':
      return numAmount * 26 / 12;
    case 'semimonthly':
      return numAmount * 2;
    case 'monthly':
      return numAmount;
    case 'quarterly':
      return numAmount / 3;
    case 'annually':
      return numAmount / 12;
    default:
      return numAmount;
  }
}

// Helper to get frequency label
function getFrequencyLabel(frequency) {
  const labels = {
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    semimonthly: 'Twice a month',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annually: 'Annually'
  };
  return labels[frequency] || frequency;
}

/**
 * ViewIncomeModal - Display income details with option to edit or delete
 */
function ViewIncomeModal({ 
  isOpen, 
  onClose, 
  income, 
  onEdit, 
  onDelete,
  currentUserId 
}) {
  if (!income) return null;

  const monthlyAmount = calculateMonthlyAmount(income.amount, income.pay_frequency);
  const isOwner = income.user_id === currentUserId;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Income Details" size="md">
      <div className="view-income-modal">
        {/* Header Card */}
        <div className={`view-income-card ${!income.is_active ? 'inactive' : ''}`}>
          {/* Income Icon */}
          <div className="view-income-card-icon">
            {income.icon_url ? (
              <img src={income.icon_url} alt="" />
            ) : (
              <DollarSign size={32} />
            )}
          </div>
          <div className="view-income-card-info">
            <h2>{income.income_name}</h2>
            <div className="view-income-card-meta">
              <span>{getFrequencyLabel(income.pay_frequency)}</span>
            </div>
          </div>
          <div className="view-income-card-amount">
            <div className="amount">+{formatAmount(income.amount)}</div>
            <div className="monthly-equivalent">{formatAmount(monthlyAmount)}/mo</div>
            {!income.is_active && (
              <div className="status inactive"><EyeOff size={14} /> Inactive</div>
            )}
          </div>
        </div>

        {/* Details Section */}
        <div className="view-income-details">
          {/* Next Pay Date */}
          <div className="view-income-detail-row">
            <span className="detail-label">Next Pay Date</span>
            <span className="detail-value">
              <Calendar size={14} />
              {income.next_pay_date ? formatDate(income.next_pay_date) : 'Not set'}
            </span>
          </div>

          {/* Frequency */}
          <div className="view-income-detail-row">
            <span className="detail-label">Pay Frequency</span>
            <span className="detail-value">
              <RefreshCw size={14} />
              {getFrequencyLabel(income.pay_frequency)}
            </span>
          </div>

          {/* Owner (if in household) */}
          {income.first_name && (
            <div className="view-income-detail-row">
              <span className="detail-label">Belongs To</span>
              <span className="detail-value">
                <User size={14} />
                {isOwner ? 'You' : `${income.first_name} ${income.last_name || ''}`}
              </span>
            </div>
          )}

          {/* Shared with Household */}
          <div className="view-income-detail-row">
            <span className="detail-label">Household Sharing</span>
            <span className="detail-value">
              {income.share_with_household ? (
                <><Users size={14} /> Shared with household</>
              ) : (
                <><User size={14} /> Personal only</>
              )}
            </span>
          </div>
        </div>

        {/* Notes */}
        {income.notes && (
          <div className="view-income-notes">
            <span className="detail-label">Notes</span>
            <p>{income.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="view-income-actions">
          <div className="view-income-actions-left">
            <button 
              type="button" 
              className="btn-icon btn-icon-danger" 
              onClick={() => onDelete(income)}
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
          <div className="view-income-actions-right">
            <button 
              type="button" 
              className="btn btn-primary-soft" 
              onClick={() => onEdit(income)}
            >
              <Edit size={16} /> Edit
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

export default ViewIncomeModal;
